import React, {Suspense, useId, useRef, useState} from "react";
import {ModalSheet} from "components/ModalSheet";
import {Prose} from "components/ContentPage";
import {useMessages} from "i18n";
import type {ContentPageEntry} from "content/contentPages";
import {
	anchorAppPath,
	isPlainPrimaryClick,
	resolveResourceDialogTarget,
	type ResourceDialogTarget,
} from "nav/resourceNavigation";
import {treeRelativePath} from "seo/documentLang";
import {useAbsoluteSiteLinks} from "nav/useAbsoluteSiteLinks";
import {GuideIndex} from "./GuidesDialog.styles";

/**
 * The Resources → Guides sheet (ui#1555): one guide at a time, with the picker for the rest under it.
 *
 * **Why it exists.** ui#1540 gave the Resources disclosure a fourth column listing four content pages
 * by hand. The owner did not want a column ("get rid of column 'ghiduri' should be just a menu item
 * under 'Explorează' and when clicked it should open a dialog that allows users to select different
 * guides which load the same in the same dialog", 2026-08-27). So the column is gone, one Explore row
 * opens this, and which pages it offers comes from `content/guideCatalogue` rather than a list anyone
 * has to remember to extend.
 *
 * **The guides' own routes are untouched.** Every page here is still a real, crawlable, deep-linkable
 * URL; this is an extra reading surface, and the Explore row that opens it is an anchor at the
 * overview page, so a middle-click still lands on the page.
 *
 * **The article is a lazy chunk** — see {@link ./GuideArticle} for why that is required rather than
 * tidy. This module carries only the sheet, the picker and the selection, so the eager nav graph gains
 * no markdown parser and no page body.
 */
const GuideArticle = React.lazy(() =>
	import("nav/GuideArticle").then((module) => ({default: module.GuideArticle})));

export type GuidesDialogProps = {
	open: boolean;
	/**
	 * The guides this locale ships, overview first — `guideNavEntryFor`'s own list, so the row's href
	 * and the picker's first entry are the same page by construction. Never empty.
	 */
	choices: readonly [ContentPageEntry, ...ContentPageEntry[]];
	onClose: () => void;
	/**
	 * A link inside a guide that leads OUT of the picker — another Resources page, Features, Pricing,
	 * the cookie policy, or an ordinary route. Handed to the bar, which owns the one pending-target
	 * replay every in-place document already goes through.
	 */
	onTarget: (target: ResourceDialogTarget) => void;
	onTransitionExited: () => void;
};

export function GuidesDialog({open, choices, onClose, onTarget, onTransitionExited}: GuidesDialogProps) {
	const {nav} = useMessages();
	const titleId = useId();
	const indexId = useId();
	const bodyRef = useRef<HTMLDivElement>(null);
	const [selectedPath, setSelectedPath] = useState<string | null>(null);
	// Each OPEN starts on the overview, the page the Explore row itself points at. Adjusted during
	// render rather than in an effect: an effect would paint the guide the reader left behind for one
	// frame of the entrance transition before replacing it.
	const [openedWith, setOpenedWith] = useState(open);
	if (open !== openedWith) {
		setOpenedWith(open);
		if (open) {
			setSelectedPath(null);
		}
	}
	const selected = choices.find((choice) => choice.path === selectedPath) ?? choices[0];
	// Include the selection because `ModalSheet`'s `bodyKey` remounts the whole region when it changes,
	// and `open` because the portal body is absent while closed and remounts for each open cycle.
	useAbsoluteSiteLinks(bodyRef, `${selected.path}:${open}`);

	/**
	 * Clicks anywhere in the body — the picker below and the article above it.
	 *
	 * A picked guide SWAPS in place, which is what the dialog is for. Everything else is handed to the
	 * bar's existing resolver, so a guide that links to `/pricing` opens the pricing sheet exactly as
	 * the same link does from a Resources page. Non-plain activations (middle-click, ⌘/Ctrl, a new tab)
	 * and anything not root-relative fall through to the browser: on a claimed host these hrefs are
	 * already absolute brand URLs (`useAbsoluteSiteLinks`), and leaving is the correct outcome there.
	 */
	const onBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!isPlainPrimaryClick(event) || !(event.target instanceof Element)) {
			return;
		}
		const anchor = event.target.closest<HTMLAnchorElement>("a[href]");
		if (anchor === null || !event.currentTarget.contains(anchor)
			|| (anchor.target !== "" && anchor.target !== "_self") || anchor.hasAttribute("download")) {
			return;
		}
		const href = anchorAppPath(anchor, window.location.origin);
		if (href === null) {
			return;
		}
		// Tree-relative, for the reason {@link nav/resourceNavigation.resolveResourceDialogTarget} collapses
		// its own lookup: the same guide is `/ghid/x` on transferify.ro and `/ro/ghid/x` on the main domain.
		const path = treeRelativePath(href.split("#", 1)[0].split("?", 1)[0]);
		if (choices.some((choice) => choice.path === path)) {
			event.preventDefault();
			setSelectedPath(path);
			return;
		}
		const target = resolveResourceDialogTarget(href);
		if (target.type === "native") {
			return;
		}
		event.preventDefault();
		onTarget(target);
	};

	return (
		<ModalSheet
			open={open}
			onClose={onClose}
			onTransitionExited={onTransitionExited}
			titleId={titleId}
			title={selected.title}
			width={860}
			bodyKey={selected.path}
		>
			<Prose ref={bodyRef} onClickCapture={onBodyClick}>
				{/* No fallback: the chunk and the page's markdown both arrive in a frame or two on a warm
				    connection, and a skeleton that flashes for one frame reads as a glitch. The article
				    renders nothing until it has its document, and says so if the fetch fails. */}
				<Suspense fallback={null}>
					<GuideArticle entry={selected} labelledBy={titleId}/>
				</Suspense>
				{/* Under the article rather than above it, in the same place a content page puts its
				    related and corpus lists — and `bodyKey` scrolls the region back to the top on every
				    swap, so picking one guide always lands the reader at the start of the next. */}
				<GuideIndex aria-labelledby={indexId}>
					<h2 id={indexId}>{nav.resourcesMenu.guides}</h2>
					<ul>
						{choices.map((choice) => (
							<li key={choice.path}>
								{/* A plain anchor, not a router link: this body is inside a dialog whose own
								    capture handler owns the plain click, and on a claimed host the href is
								    rewritten to the brand origin — which a router push would ignore. */}
								<a
									href={choice.path}
									aria-current={choice.path === selected.path ? "true" : undefined}
								>
									{choice.title}
								</a>
							</li>
						))}
					</ul>
				</GuideIndex>
			</Prose>
		</ModalSheet>
	);
}
